import type { FeatureFlagValue } from '@posthog/core';
import { PostHog } from 'posthog-js';
import React from 'react';

enum PostHogFeatureFlagMap {
  EnableAdditionalExpressOnboardingOptions = 'additional-express-onboarding-options-enabled',
  EnableAlphaApps = 'enable-alpha-apps',
  EnableAppSetup = 'enable_app_setup',
  EnableBetaApps = 'enable-beta-apps',
  EnableBillingConsolidation = 'billing-consolidation',
  /**
   * Context Bar feature flags - when removing EnableContextBarCrmSidebarUnification:
   * DELETE these files:
   *   - src/legacy/components/ClientDetailsPage/ClientSummaryDetailsSidebar.tsx
   *   - src/routes/companies/details/CompanyDetailsSidebar.tsx
   *   - src/features/crm/CrmDetailsTabs.tsx
   *   - src/features/crm/CrmProperties.tsx
   * CLEANUP conditionals in:
   *   - src/routes/companies/details/CompaniesDetailsPage.tsx (sidebar conditional)
   *   - src/legacy/components/ClientDetailsPage/ClientDetailsPageContainer.tsx (sidebar conditional + remove callback props)
   */
  EnableContextBarCrmSidebarUnification = 'context_bar_crm_sidebar_unification',
  EnableContextBarCrmSidebarGlobalState = 'context_bar_crm_sidebar_global_state',
  EnableContextBarVisualUpgrades = 'context_bar_visual_upgrades',
  EnableContextBarOtherPage = 'context_bar_other_pages',
  EnableInvoiceStatusNotifications = 'enable-invoice-status-notifications',
  EnableIUAccessControl = 'iu_access_control',
  EnableMicrodepositVerification = 'enable-microdeposit-verification',
  EnableMessageEditorInput = 'message-editor-input',
  EnableOtherMessagesEditor = 'enable-other-messages-editor',
}

export type FlagContextType = Record<
  'EnableManyCompanies' | keyof typeof PostHogFeatureFlagMap,
  boolean
>;

export const DefaultFlagsContext: FlagContextType = {
  // Permanently enabled
  EnableManyCompanies: true,

  // PostHog flags
  EnableAdditionalExpressOnboardingOptions: false,
  EnableAlphaApps: false,
  EnableAppSetup: false,
  EnableBetaApps: false,
  EnableBillingConsolidation: false,
  EnableContextBarCrmSidebarUnification: false,
  EnableContextBarCrmSidebarGlobalState: false,
  EnableContextBarVisualUpgrades: false,
  EnableContextBarOtherPage: false,
  EnableInvoiceStatusNotifications: false,
  EnableIUAccessControl: false,
  EnableMicrodepositVerification: false,
  EnableMessageEditorInput: false,
  EnableOtherMessagesEditor: false,
};

/**
 * These flags are available on the server side, but are not used so we
 * purposefully exclude them
 */
type ClientOnlyFeatureFlagKeysType = 'EnableAppSetup';

export const getServerFlagsContextValue = (
  posthogFlags: Record<PostHogFeatureFlagMap, FeatureFlagValue>,
): Record<
  Exclude<keyof FlagContextType, ClientOnlyFeatureFlagKeysType>,
  boolean
> => ({
  EnableManyCompanies: true,

  // PostHog flags
  EnableAdditionalExpressOnboardingOptions: Boolean(
    posthogFlags[
      PostHogFeatureFlagMap.EnableAdditionalExpressOnboardingOptions
    ],
  ),
  EnableAlphaApps: Boolean(posthogFlags[PostHogFeatureFlagMap.EnableAlphaApps]),
  EnableBetaApps: Boolean(posthogFlags[PostHogFeatureFlagMap.EnableBetaApps]),
  EnableBillingConsolidation: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableBillingConsolidation],
  ),
  EnableContextBarCrmSidebarUnification: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableContextBarCrmSidebarUnification],
  ),
  EnableContextBarCrmSidebarGlobalState: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableContextBarCrmSidebarGlobalState],
  ),
  EnableContextBarVisualUpgrades: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableContextBarVisualUpgrades],
  ),
  EnableContextBarOtherPage: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableContextBarOtherPage],
  ),
  EnableInvoiceStatusNotifications: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableInvoiceStatusNotifications],
  ),
  EnableIUAccessControl: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableIUAccessControl],
  ),
  EnableMicrodepositVerification: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableMicrodepositVerification],
  ),
  EnableMessageEditorInput: Boolean(posthogFlags['message-editor-input']),
  EnableOtherMessagesEditor: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableOtherMessagesEditor],
  ),
});

export const getClientFlagsContextValue = (
  posthogFlags: PostHog,
): Record<keyof FlagContextType, boolean | undefined> => ({
  EnableManyCompanies: true,

  // PostHog flags
  EnableAdditionalExpressOnboardingOptions: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAdditionalExpressOnboardingOptions,
  ),
  EnableAlphaApps: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAlphaApps,
  ),
  EnableAppSetup: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppSetup,
  ),
  EnableBetaApps: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableBetaApps,
  ),
  EnableBillingConsolidation: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableBillingConsolidation,
  ),
  EnableContextBarCrmSidebarUnification: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableContextBarCrmSidebarUnification,
  ),
  EnableContextBarCrmSidebarGlobalState: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableContextBarCrmSidebarGlobalState,
  ),
  EnableContextBarVisualUpgrades: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableContextBarVisualUpgrades,
  ),
  EnableContextBarOtherPage: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableContextBarOtherPage,
  ),
  EnableInvoiceStatusNotifications: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableInvoiceStatusNotifications,
  ),
  EnableIUAccessControl: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableIUAccessControl,
  ),
  EnableMicrodepositVerification: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableMicrodepositVerification,
  ),
  EnableMessageEditorInput: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableMessageEditorInput,
  ),
  EnableOtherMessagesEditor: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableOtherMessagesEditor,
  ),
});

export const FlagsContext = React.createContext(DefaultFlagsContext);
